import type {
  Account,
  RestoreInstanceVolumeInput,
  Snapshot,
  InventorySnapshotVolume,
} from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import { useEnvironment } from '@/contexts/useEnvironment';

import { CrossCloudInstanceAdapter } from './crossCloudInstanceAdapter';
import { CrossCloudVolumeAdapter } from './crossCloudVolumeAdapter';
import { SelectTargetAccountStep } from './steps/selectTargetAccount';

import type { Encryption } from '../encryption/encryptionComponent';
import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export type VolumeConfigState = RestoreInstanceVolumeInput & {
  encryption?: Encryption;
  keepOriginalTags: boolean;
  keepOriginalSettings: boolean;
};

export interface RestoreEc2InstanceState {
  resourceId: string;
  resourceName: string;
  snapshot: Snapshot;
  supportedCloudProviders: Provider[];
  restoreAccount?: Account;
  sharedEncryption?: Encryption;
  vpc?: string;
  regionName?: string;
  AWSSubnetId?: string;
  securityGroupIds?: string[];
  instanceType?: string;
  tags: Record<string, string>;
  keepOriginalTags: boolean;
  volumesConfigState?: VolumeConfigState[];
  volumes: InventorySnapshotVolume[];
}

const restoreEc2InstanceFlow: Step<RestoreEc2InstanceState> = {
  id: 'select_target_account_step',
  Component: SelectTargetAccountStep,
  next: [
    {
      id: 'instance_configuration',
      Component: CrossCloudInstanceAdapter,
      next: [
        {
          id: 'volume_configuration',
          Component: CrossCloudVolumeAdapter,
        },
      ],
    },
  ],
};

export const RestoreEc2InstanceWizard = ({
  resourceName,
  snapshot,
  volumes,
  onClose,
  onBack,
}: {
  resourceName: string;
  snapshot: Snapshot;
  volumes: InventorySnapshotVolume[];
  onClose: () => void;
  onBack: () => void;
}) => {
  const { isDemo, isDev } = useEnvironment();

  const volumesSettings: VolumeConfigState[] = volumes.map((vol) => ({
    ...vol,
    tags: vol.tags || {},
    isEncrypted: true,
    volumeEncryptionKeyId: '',
    encryptionKeyId: undefined,
    keepOriginalTags: true,
    keepOriginalSettings: true,
  }));

  return (
    <Wizard<RestoreEc2InstanceState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreEc2InstanceFlow]}
      initialState={{
        snapshot,
        resourceId: snapshot.resourceId,
        resourceName,
        supportedCloudProviders:
          isDemo || isDev
            ? [Provider.Aws, Provider.Azure, Provider.Gcp]
            : [Provider.Aws],
        volumes,
        sharedEncryption: {
          isEncrypted: true,
          encryptionKeyId: undefined,
          arn: undefined,
          mode: 'select',
        },
        regionName:
          snapshot.resource?.properties?.awsEc2?.volumes?.[0]?.region ||
          'us-east-1',
        AWSSubnetId: snapshot.resource?.properties?.awsEc2?.subnetId,
        securityGroupIds:
          snapshot.resource?.properties?.awsEc2?.securityGroupIds,
        instanceType: snapshot.resource?.properties?.awsEc2?.instanceType,
        tags: snapshot.resource?.tags || {},
        keepOriginalTags: true,
        volumesConfigState: volumesSettings,
      }}
      title={`Restore EC2 Instance`}
      stepperLabels={[
        'Restore account',
        'Instance configuration',
        'Volume configuration',
      ]}
    />
  );
};
